import React, { useEffect, useState } from "react";
import GridTable from "cvmgmt-cube-grid-table";
import PanelComponent from "../PanelComponent/PanelComponent";
import ManageContext from "./ManageContext";
import { useParams } from "react-router-dom";
import { getContextTableData } from "../../services/RequestAccess/RequestAccess";
import { ColumnFilter, SortingState } from "@tanstack/react-table";
import { Pagination } from "cprime-foundational-components";
import { formatFilterValue } from "../../utils/formatFilters";
import { GridTableDataType } from "cvmgmt-cube-grid-table/dist/types/src/GridTable/types";
import { Box } from "@mui/material";
import { useIdToken } from "../../hooks/useAuthSession";

interface ContextProps {
  appId: number;
  isCreateContext: boolean;
  setIsCreateContext: any;
  isEditable?: boolean;
}

const ContextManage: React.FC<ContextProps> = ({
  appId,
  isCreateContext,
  setIsCreateContext,
  isEditable = true,
}) => {
  const [refresh, setRefresh] = useState(0);
  const { id } = useParams();
  const idToken = useIdToken();
  const [openPanel, setOpenPanel] = useState<boolean>(isCreateContext);
  const [editForm, setEditForm] = useState<boolean>(isCreateContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filters, setFilters] = useState<ColumnFilter[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 25 });
  const [gridData, setGridData] = useState<GridTableDataType | any>();

  useEffect(() => {
    if (isCreateContext) setOpenPanel(isCreateContext);
  }, [isCreateContext]);

  useEffect(() => {
    fetchData();
  }, [filters, sorting, pagination, refresh]);

  const fetchData = async () => {
    const appId = id?.split("-")[1];
    setIsLoading(true);
    try {
      const resp = await getContextTableData(
        {
          sortingFilter: {
            filter:
              filters?.length > 0
                ? filters.map((item: any) =>
                    formatFilterValue(item.value, item.id),
                  )
                : [],
            sort: sorting.map((item: any) => {
              return {
                columnName: item.id,
                type: item.desc ? "DESC" : "ASC",
              };
            }),
            page: {
              limit: pagination.rowsPerPage,
              offset: pagination?.page * pagination?.rowsPerPage,
            },
          },
        },
        appId,
      );

      const mappedRows = (resp?.data?.rows ?? []).map((row: any) => {
        return {
          ...row,
          action: row?.action?.map((action: any) => {
            return {
              ...action,
              disabled: isEditable
                ? action?.disabled
                : action?.disabled || action?.value === "Edit",
            };
          }),
        };
      });

      setGridData({
        ...(resp?.data ?? {}),
        columns: resp?.data?.columns?.map((column: any) => {
          return {
            ...column,
            size: column?.size ?? 200,
            minSize: column?.minSize ?? 200,
          };
        }),
        rows: mappedRows,
        totalCount: resp?.data?.totalCount ?? mappedRows.length,
      });
    } catch {
      setGridData({ rows: [], totalCount: 0, columns: [] });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ width: 1, bgcolor: "white", borderRadius: "4px" }}>
      {idToken && (
        <>
          <GridTable
            style={{
              height: `calc(100vh - ${!isEditable ? 36.2 : 34}rem)`,
              maxWidth: "calc(100vw - 4rem)",
            }}
            refresh={refresh}
            customPayload={true}
            isCustomizeColumns={false}
            title="Context Details"
            data={gridData}
            isPagination={true}
            isFiltering={true}
            isSorting={true}
            filters={filters}
            sorting={sorting}
            setSorting={setSorting}
            setFilters={setFilters}
            onRowClick={(_, row: any) => {}}
            cellActions={[
              {
                event: "Edit",
                callback: (event, row) => {
                  if (!isEditable) return;
                  const rowData = row?.row?.original ?? row?.original ?? row;
                  setOpenPanel(true);
                  setEditForm(rowData);
                },
              },
            ]}
            showLoader={false}
            loading={isLoading}
          />
          <Pagination
            sx={{ width: "100%" }}
            {...{
              ...pagination,
              count: gridData?.totalCount ?? 0,
              rowsPerPageOptions: [25, 50, 100, 250],
              onPageChange: (e, number) => {
                setPagination({ ...pagination, page: number });
              },
              onRowsPerPageChange: (evt) => {
                setPagination({
                  page: 0,
                  rowsPerPage: Number(evt.target.value),
                });
              },
            }}
          />
        </>
      )}

      {openPanel && (
        <PanelComponent
          open={openPanel}
          title={`${isCreateContext ? "Create" : "Edit"} Context`}
          isEdit={false}
          onClose={() => {
            setOpenPanel(false);
            setIsCreateContext(false);
          }}
          BodyComponent={
            <ManageContext
              appId={appId}
              isEdit={!isCreateContext}
              formData={editForm}
              onClose={() => {
                setOpenPanel(false);
                setIsCreateContext(false);
              }}
              incrementTableRefresh={() => setRefresh((prev) => prev + 1)}
            />
          }
          tabValue={7}
        />
      )}
    </Box>
  );
};

export default ContextManage;
